import {
	Card,
	Descriptions,
	Tag,
	Button,
	Space,
	Divider,
	Typography,
	Row,
	Col,
	Table,
	notification,
	Spin,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PiPencilDuotone, PiArrowLeftDuotone } from "react-icons/pi";
import AppContext from "../../store/app.context";
import { requestGetServiceById, requestGetServiceGroupList } from "../../services/admin.service";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;

function ServiceShowPage() {
	const context = useContext(AppContext);
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(false);
	const [service, setService] = useState(null);
	const [serviceGroups, setServiceGroups] = useState([]);

	useEffect(() => {
		document.title = "Cloudiva Console | Service Details";
		fetchServiceGroups();
		fetchService();
	}, [id]);

	const fetchServiceGroups = () => {
		requestGetServiceGroupList({
			limit: 100,
			order: "name:asc",
		})
			.then((response) => {
				setServiceGroups(response.data.data);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch service groups",
				});
			});
	};

	const fetchService = () => {
		setLoading(true);
		requestGetServiceById(id)
			.then((response) => {
				setService(response.data.data);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch service details",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const getServiceGroupName = (groupId) => {
		const group = serviceGroups.find((g) => g._id === groupId);
		return group ? group.name : "Unknown";
	};

	const featureColumns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
		},
	];

	const planColumns = [
		{
			title: "Code",
			dataIndex: "code",
			key: "code",
			render: (text) => <Typography.Text code>{text}</Typography.Text>,
		},
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
		},
		{
			title: "Base Price",
			dataIndex: ["price"],
			key: "price",
			render: (price, record) => (
				<Space>
					<Typography.Text>{formatCurrency(price.amount, record.currency)}</Typography.Text>
					<Typography.Text type="secondary">
						/{price.unit.toLowerCase()}/{price.interval.toLowerCase()}
					</Typography.Text>
				</Space>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => <Tag color={status === "ACTIVE" ? "success" : "error"}>{status}</Tag>,
		},
	];

	const regionColumns = [
		{
			title: "Code",
			dataIndex: "code",
			key: "code",
		},
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "Base Price",
			dataIndex: ["price"],
			key: "price",
			render: (price, record) => (
				<Space>
					<Typography.Text>
						{price.type === "FIXED"
							? formatCurrency(price.amount, record.currency)
							: `${price.amount}%`}
					</Typography.Text>
					<Typography.Text type="secondary">({price.type.toLowerCase()})</Typography.Text>
				</Space>
			),
		},
	];

	const addonColumns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
		},
		{
			title: "Base Price",
			dataIndex: ["price"],
			key: "price",
			render: (price, record) => (
				<Space>
					<Typography.Text>
						{price.type === "FIXED"
							? formatCurrency(price.amount, record.currency)
							: `${price.amount}%`}
					</Typography.Text>
					<Typography.Text type="secondary">({price.type.toLowerCase()})</Typography.Text>
				</Space>
			),
		},
	];

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Spin size="large" />
			</div>
		);
	}

	if (!service) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Text>Service not found</Text>
			</div>
		);
	}

	return (
		<div>
			{/* Header */}
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/services")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						{service.name}
					</Title>
					<Button
						type="link"
						size="small"
						icon={<PiPencilDuotone />}
						onClick={() => navigate(`/services/${service._id}/edit`)}>
						Edit Service
					</Button>
				</Space>
			</div>

			{/* Basic Information */}
			<Card title="Basic Information" style={{ marginBottom: 24 }}>
				<Descriptions column={2} bordered>
					<Descriptions.Item label="Service Code">
						<strong>{service.code}</strong>
					</Descriptions.Item>
					<Descriptions.Item label="Status">
						<Tag color={service.status === "ACTIVE" ? "success" : "error"}>{service.status}</Tag>
					</Descriptions.Item>
					<Descriptions.Item label="Service Group">
						{getServiceGroupName(service.groupId)}
					</Descriptions.Item>
					<Descriptions.Item label="Version">{service.version}</Descriptions.Item>
					<Descriptions.Item label="Description" span={2}>
						{service.description}
					</Descriptions.Item>
					{service.metadata && Object.keys(service.metadata).length > 0 && (
						<Descriptions.Item label="Metadata" span={2}>
							<pre style={{ fontSize: 12, background: "#FBFBFB", padding: 8, borderRadius: 4 }}>
								{JSON.stringify(service.metadata, null, 2)}
							</pre>
						</Descriptions.Item>
					)}
				</Descriptions>
			</Card>

			{/* Features */}
			{service.features && service.features.length > 0 && (
				<Card title="Features" style={{ marginBottom: 24 }}>
					<Table
						columns={featureColumns}
						dataSource={service.features}
						rowKey="name"
						pagination={false}
						size="small"
					/>
				</Card>
			)}

			{/* Plans */}
			{service.plans && service.plans.length > 0 && (
				<Card title="Service Plans" style={{ marginBottom: 24 }}>
					<Table
						columns={planColumns}
						dataSource={service.plans}
						rowKey="name"
						pagination={false}
						size="small"
						expandable={{
							expandedRowRender: (record) => (
								<div style={{ padding: 16 }}>
									<Row gutter={[16, 16]}>
										<Col span={12}>
											<strong>Pricing Details:</strong>
											<ul style={{ marginTop: 8 }}>
												<li>Currency: {record.pricing?.currency || "USD"}</li>
												<li>Billing Period: {record.pricing?.billingPeriod || "N/A"}</li>
												<li>Free Trial Days: {record.pricing?.freeTrialDays || 0}</li>
												<li>
													Setup Fee:{" "}
													{formatCurrency(record.pricing?.setupFee || 0, record.pricing?.currency)}
												</li>
											</ul>
										</Col>
										<Col span={12}>
											{record.limits && Object.keys(record.limits).length > 0 && (
												<div>
													<strong>Limits:</strong>
													<pre
														style={{
															fontSize: 12,
															background: "#FBFBFB",
															padding: 8,
															borderRadius: 4,
															marginTop: 8,
														}}>
														{JSON.stringify(record.limits, null, 2)}
													</pre>
												</div>
											)}
										</Col>
									</Row>
								</div>
							),
						}}
					/>
				</Card>
			)}

			{/* Regions */}
			{service.regions && service.regions.length > 0 && (
				<Card title="Available Regions" style={{ marginBottom: 24 }}>
					<Table
						columns={regionColumns}
						dataSource={service.regions}
						rowKey="code"
						pagination={false}
						size="small"
					/>
				</Card>
			)}

			{/* Add-ons */}
			{service.addons && service.addons.length > 0 && (
				<Card title="Add-ons" style={{ marginBottom: 24 }}>
					<Table
						columns={addonColumns}
						dataSource={service.addons}
						rowKey="name"
						pagination={false}
						size="small"
						expandable={{
							expandedRowRender: (record) => (
								<div style={{ padding: 16 }}>
									<Row gutter={[16, 16]}>
										<Col span={12}>
											<strong>Pricing Details:</strong>
											<ul style={{ marginTop: 8 }}>
												<li>Currency: {record.pricing?.currency || "USD"}</li>
												<li>Billing Period: {record.pricing?.billingPeriod || "N/A"}</li>
												<li>
													Setup Fee:{" "}
													{formatCurrency(record.pricing?.setupFee || 0, record.pricing?.currency)}
												</li>
											</ul>
										</Col>
										<Col span={12}>
											{record.configuration && Object.keys(record.configuration).length > 0 && (
												<div>
													<strong>Configuration:</strong>
													<pre
														style={{
															fontSize: 12,
															background: "#FBFBFB",
															padding: 8,
															borderRadius: 4,
															marginTop: 8,
														}}>
														{JSON.stringify(record.configuration, null, 2)}
													</pre>
												</div>
											)}
										</Col>
									</Row>
								</div>
							),
						}}
					/>
				</Card>
			)}
		</div>
	);
}

export default ServiceShowPage;
